import React, { useEffect, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { CloudUploadFill } from 'react-bootstrap-icons';
import { AzureRepoIntegrationPayload } from '../types/project';
import './RepositoryUpload.css';

interface RepositoryUploadProps {
  title?: string;
  description?: string;
  onUpload: (file: File, metadata?: { name?: string; description?: string; azureRepoName?: string; azureBranch?: string }) => Promise<void> | void;
  onIntegrateAzureRepo?: (payload: AzureRepoIntegrationPayload) => Promise<void> | void;
  defaultName?: string;
  defaultDescription?: string;
  defaultAzureRepoName?: string;
  defaultAzureRepoUrl?: string;
  defaultAzureBranch?: string;
  disableRepositorySource?: boolean;
  uploading?: boolean;
  compact?: boolean;
  isAzureDevOps?: boolean;
  tutorialHighlightTargetId?: string | null;
}

const MAX_REPOSITORY_ZIP_BYTES = 100 * 1024 * 1024;

const RepositoryUpload: React.FC<RepositoryUploadProps> = ({
  title = 'Upload Repository',
  description = 'Attach a .zip archive of your project repository.',
  onUpload,
  onIntegrateAzureRepo,
  defaultName,
  defaultDescription,
  defaultAzureRepoName,
  defaultAzureRepoUrl,
  defaultAzureBranch,
  disableRepositorySource = false,
  uploading = false,
  compact = false,
  isAzureDevOps = false,
  tutorialHighlightTargetId = null,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>(defaultName ?? '');
  const [repoDescription, setRepoDescription] = useState<string>(defaultDescription ?? '');
  const [error, setError] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'local' | 'azure'>(isAzureDevOps ? 'azure' : 'local');
  const [azureRepoName, setAzureRepoName] = useState(defaultAzureRepoName ?? '');
  const [azureRepoUrl, setAzureRepoUrl] = useState(defaultAzureRepoUrl ?? '');
  const [azureBranch, setAzureBranch] = useState(defaultAzureBranch ?? 'main');

  const isAzureMode = isAzureDevOps && uploadMode === 'azure';

  // Initialize upload mode based on project type when component mounts or isAzureDevOps changes
  useEffect(() => {
    if (isAzureDevOps) {
      setUploadMode('azure');
    } else {
      setUploadMode('local');
    }
  }, [isAzureDevOps]);

  useEffect(() => {
    setAzureRepoUrl(defaultAzureRepoUrl ?? '');
  }, [defaultAzureRepoUrl]);

  useEffect(() => {
    setAzureRepoName(defaultAzureRepoName ?? '');
  }, [defaultAzureRepoName]);

  useEffect(() => {
    setAzureBranch(defaultAzureBranch ?? 'main');
  }, [defaultAzureBranch]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      if (isAzureMode) {
        if (!onIntegrateAzureRepo) {
          setError('Azure DevOps integration is not available for this project');
          return;
        }
        if (!azureRepoUrl.trim()) {
          setError('Provide the Azure DevOps repository URL');
          return;
        }
        setError(null);
        await onIntegrateAzureRepo({
          azureRepoUrl: azureRepoUrl.trim(),
          branch: azureBranch.trim() || 'main',
        });
        setAzureRepoUrl('');
        setAzureBranch('main');
        return;
      }
      if (!file) {
        setError('Select a .zip file before uploading');
        return;
      }
      if (isAzureDevOps && uploadMode === 'local') {
        if (!azureRepoName.trim()) {
          setError('Provide the Azure DevOps repository name');
          return;
        }
        if (!azureBranch.trim()) {
          setError('Provide the Azure DevOps branch name');
          return;
        }
      }
      setError(null);
      await onUpload(file, {
        name: name || undefined,
        description: repoDescription || undefined,
        azureRepoName: isAzureDevOps && uploadMode === 'local' ? azureRepoName.trim() : undefined,
        azureBranch: isAzureDevOps && uploadMode === 'local' ? azureBranch.trim() : undefined,
      });
      setFile(null);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Upload failed';
      setError(message);
    }
  };

  const sourceDisabled = disableRepositorySource || uploading;
  const submitDisabled = sourceDisabled || (isAzureMode ? !azureRepoUrl.trim() : !file);
  const getTutorialClassName = (targetId: string, className = ''): string => {
    const classes = [className, 'ps-tutorial-target'];
    if (tutorialHighlightTargetId === targetId) {
      classes.push('ps-tutorial-target--active');
    }
    return classes.filter(Boolean).join(' ');
  };

  const formContent = (
    <Form onSubmit={handleSubmit}>
      <Form.Group
        id="onboarding-repository-name"
        controlId="repoName"
        className={getTutorialClassName('onboarding-repository-name', 'mb-3')}
      >
        <Form.Label>Repository Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Describe the repository"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Form.Group>

      <Form.Group
        id="onboarding-repository-description"
        controlId="repoDescription"
        className={getTutorialClassName('onboarding-repository-description', 'mb-3')}
      >
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Optional repository description"
          value={repoDescription}
          onChange={(e) => setRepoDescription(e.target.value)}
        />
      </Form.Group>

      {isAzureDevOps && (
        <Form.Group controlId="repoUploadMode" className="mb-3">
          <Form.Label>Repository Source</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="repo-upload-local"
              name="repoUploadMode"
              label="Local archive"
              value="local"
              checked={uploadMode === 'local'}
              disabled={sourceDisabled}
              onChange={() => {
                setUploadMode('local');
                setFile(null);
                setError(null);
              }}
            />
            <Form.Check
              type="radio"
              id="repo-upload-azure"
              name="repoUploadMode"
              label="Azure DevOps repository"
              value="azure"
              checked={uploadMode === 'azure'}
              disabled={sourceDisabled}
              onChange={() => {
                setUploadMode('azure');
                setFile(null);
                setError(null);
              }}
            />
          </div>
        </Form.Group>
      )}

      {uploadMode === 'local' && (
        <>
          <Form.Group
            id="onboarding-repository-file"
            controlId="repoFile"
            className={getTutorialClassName('onboarding-repository-file', 'mb-3')}
          >
            <Form.Label>Select Archive (.zip)</Form.Label>
            <Form.Control
              type="file"
              accept=".zip"
              disabled={sourceDisabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const selected = e.currentTarget.files?.[0];
                if (selected && !selected.name.toLowerCase().endsWith('.zip')) {
                  setFile(null);
                  setError(`"${selected.name}" is not supported. Upload a .zip repository archive.`);
                  e.currentTarget.value = '';
                  return;
                }
                if (selected && selected.size === 0) {
                  setFile(null);
                  setError(`"${selected.name}" is empty. Please choose a non-empty .zip file.`);
                  e.currentTarget.value = '';
                  return;
                }
                if (selected && selected.size > MAX_REPOSITORY_ZIP_BYTES) {
                  setFile(null);
                  setError(`"${selected.name}" is too large. Repository archives must be 100 MB or smaller.`);
                  e.currentTarget.value = '';
                  return;
                }
                setError(null);
                setFile(selected ?? null);
              }}
            />
          </Form.Group>
          {isAzureDevOps && (
            <div className="border rounded p-3 mb-3 bg-light">
              <h6 className="mb-3">Azure DevOps Repository</h6>
              <Form.Group controlId="azureRepoName" className="mb-3">
                <Form.Label>Repository Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Repository name"
                  value={azureRepoName}
                  disabled={sourceDisabled}
                  onChange={(e) => setAzureRepoName(e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="azureRepoBranchLocal" className="mb-0">
                <Form.Label>Branch Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="main"
                  value={azureBranch}
                  disabled={sourceDisabled}
                  onChange={(e) => setAzureBranch(e.target.value)}
                />
              </Form.Group>
            </div>
          )}
        </>
      )}

      {uploadMode === 'azure' && isAzureDevOps && (
        <div className="border rounded p-3 mb-3 bg-light">
          <h6 className="mb-3">Azure DevOps Repository</h6>
          <Form.Group controlId="azureRepoUrl" className="mb-3">
            <Form.Label>Repository URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://dev.azure.com/{org}/{project}/_git/{repo}"
              value={azureRepoUrl}
              disabled={sourceDisabled}
              onChange={(e) => setAzureRepoUrl(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="azureRepoBranch" className="mb-0">
            <Form.Label>Branch</Form.Label>
            <Form.Control
              type="text"
              placeholder="main"
              value={azureBranch}
              disabled={sourceDisabled}
              onChange={(e) => setAzureBranch(e.target.value)}
            />
          </Form.Group>
        </div>
      )}

      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      <div className="repo-upload-actions">
        <Button
          id="onboarding-repository-upload"
          type="submit"
          disabled={submitDisabled}
          variant="primary"
          className={getTutorialClassName('onboarding-repository-upload', 'repo-upload-submit')}
        >
          {uploading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <CloudUploadFill className="me-2" />
              {isAzureMode ? 'Integrate' : 'Upload'}
            </>
          )}
        </Button>
      </div>
    </Form>
  );

  if (compact) {
    return formContent;
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="mb-3">
          <h5 className="mb-1">{title}</h5>
          <div className="text-muted small">{description}</div>
        </div>
        {formContent}
      </Card.Body>
    </Card>
  );
};

export default RepositoryUpload;
